import { BadgeConfig, CardData } from 'flows/core/types/cards';
import { ItemKinds } from 'types';
import { DataForCards } from 'flows/core/hooks/use-data-for-cards';
import { EssentialCards } from 'flows/core/types/layouts';
import { Badges, itemOfKind } from './utils';
import { ItemStatuses } from 'constants/statuses';

const PUBLIC_GRID_IMPORTANCE_SCORE = 260_000;

/**
 * Public Grid has no item kind of its own: `Items::PublicGrid` is not being
 * created (decided 2026-10-16). The webhook records an enrolment against the
 * resident's existing proof-of-electric item instead, so that item is the
 * status of Connect power.
 *
 * The building's own proof flow writes the same item, and that is not a
 * conflict — both mean the one thing this card reports, that electricity for
 * the new home is handled. It does mean the two are indistinguishable from the
 * checklist: nothing here can tell a Public Grid enrolment from an uploaded
 * bill, and separating them would need metadata the webhook does not write.
 */
const KIND = ItemKinds.RESIDENT_ONBOARDING_PROOF_OF_ELECTRIC;

// Re-exported so the suppression sites can keep importing the flag from
// alongside the predicate they use it with. Defined in the flow's own
// constants, which `useDataForCards` can read without an import cycle.
export { PUBLIC_GRID_FEATURE_FLAG } from 'flows/public-grid/constants';

/**
 * Whether the division has ruled electricity out.
 *
 * Absent config permits: it is missing entirely wherever the property's entity
 * is not a division, and `/utilities` has always treated that case as "show the
 * electric options" rather than "hide them" — Public Grid takes over the same
 * slot. Only an explicit `electric` that is not `true` is an objection.
 */
const electricNotRuledOut = (
  utilitiesConfig?: { electric?: boolean | null } | null
): boolean => utilitiesConfig == null || utilitiesConfig.electric === true;

/**
 * The single source of truth for "Public Grid owns electricity for this move".
 *
 * Every place that suppresses our own electric UI must read this same predicate,
 * so the Public Grid card and the electric options it replaces can never both
 * be visible (or both be hidden).
 *
 * All three inputs are required, including the two that may be `undefined`:
 * forgetting either is how a resident ends up with no way to set up electricity
 * at all, so the type makes you pass them.
 *
 * There is deliberately no `isUPO` input. The rule is the same for both: a
 * missing utilities config permits, an explicit `electric: false` blocks.
 */
export const isPublicGridActive = ({
  publicGridEnabled,
  utilitiesConfig,
  publicGridEligible,
}: {
  publicGridEnabled: boolean;
  /**
   * The division's utilities config, or `undefined` where the property has no
   * division. Absent is permissive — see {@link electricNotRuledOut}.
   */
  utilitiesConfig?: { electric?: boolean | null } | null;
  /**
   * Whether Public Grid can transact for this address — `usePublicGridEligibility`,
   * or `publicGridEligible` on `DataForCards`. `undefined` means not yet known,
   * which is not active: until the answer arrives the resident keeps our own
   * utility task rather than a flow that may not serve them.
   */
  publicGridEligible: boolean | undefined;
}): boolean =>
  Boolean(
    publicGridEnabled &&
      publicGridEligible &&
      electricNotRuledOut(utilitiesConfig)
  );

/**
 * An item the Public Grid webhook may have recorded a submission against.
 *
 * Structural rather than `Item` because the same proof-of-electric item reaches
 * the proof-of-utilities UI through the Zustand store, which types it loosely.
 */
interface SubmittableItem {
  status?: string | null;
  metadata?: unknown;
}

/** GraphQL types `metadata` as `JSON`, so the shape is asserted at the read. */
interface PublicGridSubmissionMetadata {
  submission?: { publicgrid_submitted_at?: string | null } | null;
}

/**
 * Whether the webhook has recorded an enrolment against this item. The one
 * thing that tells a Public Grid submission from an uploaded bill — the item's
 * own status cannot, since the building's proof flow writes the same statuses.
 */
const hasPublicGridSubmission = ({ metadata }: SubmittableItem): boolean =>
  Boolean(
    (metadata as PublicGridSubmissionMetadata | null | undefined)?.submission
      ?.publicgrid_submitted_at
  );

/** The statuses that mean "the resident still has to do something". */
const UNSUBMITTED_STATUSES: string[] = [
  ItemStatuses.STARTED,
  ItemStatuses.NOT_STARTED,
];

/**
 * Whether the resident has finished Public Grid's flow and is waiting on an
 * approval that the item's own status cannot yet express.
 *
 * Where the property requires proof *and* manual approval, the webhook leaves
 * the item `started` on enrolment: it only becomes `validating` when the proof
 * document arrives on a later webhook, and `completed` when the partner
 * approves. Between those two the resident has done everything we ask of them,
 * so `started` alone would badge the task as outstanding. The webhook writes
 * `metadata.submission.publicgrid_submitted_at` at enrolment, which is the only
 * signal that distinguishes "not started yet" from "submitted, nothing back".
 *
 * `info_requested`, `validating`, `completed` and `cancelled` are left alone —
 * each already says more than the marker does, and a rejection outranks a
 * submission the partner has since sent back.
 */
export const isAwaitingPublicGridApproval = (item: SubmittableItem): boolean =>
  hasPublicGridSubmission(item) &&
  UNSUBMITTED_STATUSES.includes(item.status ?? ItemStatuses.NOT_STARTED);

/**
 * The status the UI shows for a proof-of-electric item: its own, except while a
 * Public Grid submission is waiting on approval, which reads as In review.
 *
 * Badges, importance scores and list placement use this. It never picks a page
 * *for the item it describes* — `getProofOfUtilsRoute` would send a
 * `validating` resident to the proof summary, which has no upload of theirs to
 * show. Where that destination does change it changes to Public Grid itself, on
 * a condition of its own — see {@link shouldOpenPublicGrid}.
 *
 * It may still gate a decision about a *sibling* utility, which displays
 * nothing of this item: `getUtilityFinishRoute` reads all three statuses
 * through it so a pending enrolment does not count electricity as outstanding
 * and strand a resident who just finished water in the hub.
 */
export const publicGridDisplayStatus = (
  item: SubmittableItem
): string | undefined =>
  isAwaitingPublicGridApproval(item)
    ? ItemStatuses.VALIDATING
    : item.status ?? undefined;

/**
 * Whether tapping the resident's electric task should open Public Grid itself
 * rather than one of our own pages.
 *
 * Those pages are all about collecting or displaying a document the resident
 * never gave us: a Public Grid enrolment is recorded by webhook, so the set-up
 * page would ask them to start something they have finished, the summary would
 * show an upload they never made, and the rejection page would ask them to
 * replace it. Public Grid returns `flowType: dashboard` for someone they
 * already know, so the embed signs them into their account rather than
 * restarting sign-up — which is where a resident goes whether their enrolment
 * is waiting on approval, approved, or sent back for more information
 * (Brian, 2026-10-28). Note that `info_requested` still badges Needs more: the
 * card tells them something is wrong and Public Grid is where they see what.
 *
 * `cancelled` is the exception, and keeps whatever destination it had before
 * Public Grid — in practice the set-up page, which every status this flow does
 * not handle falls through to.
 *
 * Callers add the two conditions this cannot see: that Public Grid is active
 * for the move (`isPublicGridActive`) and that the card leads to electricity
 * alone — a grouped utilities card still has water and gas behind it.
 */
export const shouldOpenPublicGrid = (item: SubmittableItem): boolean =>
  hasPublicGridSubmission(item) && item.status !== ItemStatuses.CANCELLED;

const PUBLIC_GRID_COMMON_STATIC_DATA = {
  subtype: 'PG',
  icon: 'LIGHT',
  title: 'Connect power',
  description: 'Set up electricity for your new home before you move in.',
  action: {
    type: 'internal',
    // The iframe host, whatever the status. There is no intro page: tapping the
    // card goes straight there, and that page mints the embed URL on mount.
    // Minting is never done from the checklist — the token lives 600s, so one
    // per card impression would mostly expire unused.
    //
    // A resident who has finished still lands here (Brian, 2026-10-16): Public
    // Grid returns `flowType: dashboard` for someone they already know, so the
    // URL signs them into their account rather than restarting sign-up.
    route: '/public-grid',
  },
} as const;

/**
 * Rolls the resident's electric items up to the one status the card displays.
 * Ordered most-urgent first so a single unresolved item keeps the card out of
 * the completed list — a move can carry more than one (roommates each get
 * their own), and any of them still needing attention means the card does.
 */
export const reducePublicGridStatus = (
  statuses: string[]
): string | undefined => {
  if (statuses.length === 0) return undefined;
  if (statuses.includes(ItemStatuses.INFO_REQUESTED))
    return ItemStatuses.INFO_REQUESTED;
  if (
    statuses.includes(ItemStatuses.STARTED) ||
    statuses.includes(ItemStatuses.NOT_STARTED)
  )
    return ItemStatuses.STARTED;
  if (statuses.includes(ItemStatuses.VALIDATING))
    return ItemStatuses.VALIDATING;
  if (statuses.includes(ItemStatuses.COMPLETED)) return ItemStatuses.COMPLETED;
  if (statuses.includes(ItemStatuses.CANCELLED)) return ItemStatuses.CANCELLED;
  return undefined;
};

export const statusToBadge = (status?: string): BadgeConfig | undefined => {
  switch (status) {
    case ItemStatuses.VALIDATING:
      return Badges.IN_REVIEW;
    case ItemStatuses.INFO_REQUESTED:
      return Badges.NEEDS_MORE;
    case ItemStatuses.COMPLETED:
      return Badges.DONE;
    case ItemStatuses.CANCELLED:
      return Badges.CANCELLED;
    default:
      return undefined;
  }
};

const isResolved = (status?: string) =>
  status === ItemStatuses.COMPLETED || status === ItemStatuses.CANCELLED;

/**
 * TEMPORARY card. Final placement is unresolved — the PRD puts Public Grid
 * inside the Connect Utilities step while the tech spec makes it a sibling
 * card, so this renders as a sibling until that is settled.
 *
 * Visibility is deliberately NOT gated on `enabledItemDefOfKind` the way the
 * tech spec specifies. There is no `public_grid` item definition to enable and
 * there will not be one, so that gate could never pass: the card shows when the
 * Growthbook flag is on and Public Grid can serve the resident's address. The
 * flag is therefore the only rollback — disabling an item definition does
 * nothing here.
 */
export const makeEssentialsTabPublicGridCards = ({
  items,
  utilitiesConfig,
  publicGridEnabled,
  publicGridEligible,
  isUPO,
}: DataForCards & {
  publicGridEnabled: boolean;
  /**
   * UPO moves get no sibling card. Their electric entry points — the row in the
   * proof-of-utilities hub and the per-utility card — already exist under their
   * own conditions and are re-pointed at Public Grid, so adding a card here
   * would be a second, duplicate way in. Introducing no new visibility logic is
   * the whole point of routing rather than suppressing.
   */
  isUPO?: boolean;
}): EssentialCards => {
  const toDoCards: CardData[] = [];
  const completedCards: CardData[] = [];

  if (isUPO) return { toDoCards, completedCards };

  if (
    !isPublicGridActive({
      publicGridEnabled,
      utilitiesConfig,
      publicGridEligible,
    })
  ) {
    return { toDoCards, completedCards };
  }

  /**
   * `declined_item_types.already_done` is deliberately not read, even though
   * Public Grid's webhook sets `utility` there on electric submission and it is
   * the only signal this card could get at a property with no proof-of-electric
   * item. The flag is overloaded: the resident's own "All done!" button on
   * `/utilities` writes the same value, and with electric hidden from that page
   * they are attesting water and gas alone. The two writers are
   * indistinguishable, so reading it would show Done to residents who set up no
   * power. Needs an unambiguous signal from the service instead.
   */
  const electricItems = items.filter(itemOfKind(KIND));
  const status = reducePublicGridStatus(
    electricItems.map((item) => publicGridDisplayStatus(item) ?? '')
  );
  const badge = statusToBadge(status);
  const required = Boolean(utilitiesConfig?.electricRequired);

  const card: CardData = {
    type: 'INITIATE',
    data: {
      ...PUBLIC_GRID_COMMON_STATIC_DATA,
      required,
      ...(status ? { status } : {}),
      ...(badge ? { badge } : {}),
      ...(status === ItemStatuses.COMPLETED ? { ctaText: 'View task' } : {}),
    },
    identifier: isResolved(status)
      ? `public_grid_initiate_${status}`
      : 'public_grid_initiate',
    importanceScore: PUBLIC_GRID_IMPORTANCE_SCORE,
  };

  if (isResolved(status)) {
    completedCards.push(card);
  } else {
    toDoCards.push(card);
  }

  return { toDoCards, completedCards };
};
